import React, { Fragment, PureComponent } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators, compose } from 'redux';
import { connect } from 'react-redux';
import { withRouter, Redirect } from 'react-router-dom';
import { logout, sendLoginLink } from '../../store/actions';
import capitalize from 'lodash/capitalize';
import qs from 'qs';
import seo from '../../services/seo';
import * as analytics from '../../services/analytics';
import * as yup from 'yup';
import { Formik } from 'formik';
import space from '../../constants/space';
import chubbLogoWhite from './chubb-logo-white.svg';
import chubbLogoDark from './chubb-logo-dark.svg';
import './LoginView.css';

const propTypes = {
  sendLoginLink: PropTypes.func,
};

class LoginView extends PureComponent {
  constructor(props) {
    super(props);
    this.formValidationSchema = yup.object().shape({
      email: yup.string().email().required('Email is required'),
    });
    this.state = {
      email: '',
      isEmailSent: false,
      isInProgress: false,
    };
  }

  componentDidMount() {
    seo.setProps({
      title: `${capitalize(space)} Broker Tool`,
    });
    if (!this.isAuthedAsBroker) {
      this.props.logout();
    } else {
      analytics.track('Broker Portal - Login Redirected');
    }
    analytics.page('Broker Portal - Login');
  }

  handleInitialValidation = ({ initialValues }) => {
    return this.formValidationSchema.isValidSync(initialValues);
  };

  handleChange = (e) => {
    const { handleChange } = this.form;
    this.setState({
      isEmailSent: false,
      email: e.target.value,
    });
    handleChange(e);
  };

  handleSubmit = (values) => {
    const { email } = values;
    analytics.page('Broker Portal - Login Link Submitted');
    this.setState({
      isInProgress: true,
    });
    this.props
      .sendLoginLink(email)
      .then(() => {
        analytics.page('Broker Portal - Login Link Sent');
        this.setState({
          isEmailSent: true,
          isInProgress: false,
        });
      })
      .catch((error) => {
        console.error(error);
        analytics.page('Broker Portal - Login Link Error');
      });
  };

  get initialFormValues() {
    return { email: '' };
  }

  get panelSuccess() {
    return (
      <Fragment>
        <div className="LoginView-successHeading">
          <i className="LoginView-successHeading-icon fas fa-check-circle" />
          <h2 className="LoginView-successHeading-heading">
            We sent your link to:
            <small className="LoginView-successHeading-email">
              {this.state.email}
            </small>
          </h2>
        </div>
        <p className="LoginView-successMessage">
          Check your email for a special link to log into the Chubb Broker
          Dashboard. Your special link will expire in 24 hours, and can only be
          used one time.
        </p>
      </Fragment>
    );
  }

  get panelDefault() {
    return (
      <Fragment>
        <h2 className="LoginView-signInPanel-subheading">
          Get a special link by email to log into your account.
        </h2>
        <p className="LoginView-signInPanel-help">
          Typing out a proper, secure password can be a pain. We can take away a
          little bit of that pain by sending you a special link that will
          automatically sign you into your account.
        </p>
        <Formik
          initialValues={this.initialFormValues}
          isInitialValid={this.handleInitialValidation}
          validationSchema={this.formValidationSchema}
          onSubmit={this.handleSubmit}
          innerRef={(c) => (this.form = c)}
        >
          {({ errors, handleSubmit, isValid, touched, values }) => {
            return (
              <form
                className="LoginView-signInPanel-form"
                onSubmit={handleSubmit}
              >
                <div className="form-group">
                  <label htmlFor="email">Email address</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    placeholder="Email Address"
                    value={values.email || ''}
                    touched={touched.email ? 'true' : 'false'}
                    errors={errors.email}
                    onChange={this.handleChange}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-complete btn-cons"
                  disabled={!isValid || this.state.isInProgress}
                >
                  Submit
                </button>
              </form>
            );
          }}
        </Formik>
      </Fragment>
    );
  }

  get panelExpired() {
    return (
      <Fragment>
        <h2 className="LoginView-signInPanel-subheading has-error">
          Sign-in link expired
        </h2>
        <p className="LoginView-signInPanel-help">
          The special sign-in link expires after 24 hours, and can only be used
          once.
        </p>
        <Formik
          initialValues={this.initialFormValues}
          isInitialValid={this.handleInitialValidation}
          validationSchema={this.formValidationSchema}
          onSubmit={this.handleSubmit}
          innerRef={(c) => (this.form = c)}
        >
          {({ errors, handleSubmit, isValid, touched, values }) => {
            return (
              <form
                className="LoginView-signInPanel-form"
                onSubmit={handleSubmit}
              >
                <div className="form-group">
                  <label htmlFor="email">Email address</label>
                  <input
                    type="email"
                    className="form-control"
                    id="email"
                    placeholder="Email Address"
                    value={values.email || ''}
                    touched={touched.email}
                    errors={errors.email}
                    onChange={this.handleChange}
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-complete btn-cons"
                  disabled={!isValid}
                >
                  Send a new special link
                </button>
              </form>
            );
          }}
        </Formik>
      </Fragment>
    );
  }

  get panelContent() {
    const hasError = !!qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    }).error;
    if (this.state.isEmailSent) {
      return this.panelSuccess;
    } else if (hasError) {
      return this.panelExpired;
    } else {
      return this.panelDefault;
    }
  }

  get isAuthedAsBroker() {
    const { authType, authToken } = this.props;
    return authType === 'Bearer' && !!authToken;
  }

  render() {
    return this.isAuthedAsBroker ? (
      <Redirect to="/dashboard" />
    ) : (
      <div className="LoginView">
        <div className="LoginView-welcome">
          <img
            className="LoginView-welcome-logo"
            src={chubbLogoWhite}
            alt="Chubb logo"
          />
          <h1 className="LoginView-welcome-heading">
            Accident & Health Digital Marketing Portal
          </h1>
          <p className="LoginView-welcome-subheading">
            Digitally market insurance to your clients with ease and precision
          </p>
        </div>
        <div className="LoginView-signInPanel">
          <img
            className="LoginView-signInPanel-logo"
            src={chubbLogoDark}
            alt="Chubb logo"
          />
          <div className="LoginView-signInPanel-content">
            {this.panelContent}
          </div>
        </div>
      </div>
    );
  }
}

LoginView.propTypes = propTypes;

const mapStateToProps = (state) => ({
  authToken: state.authToken,
  authType: state.authType,
});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      logout,
      sendLoginLink,
    },
    dispatch
  );

const enhance = compose(
  withRouter,
  connect(mapStateToProps, mapDispatchToProps)
);

export default enhance(LoginView);
